import { can, SessionLike } from "../auth/can.ts";
import { ReporteChequeo } from "../models/ReporteChequeo.ts";
import {
  readClockHoursByBranch,
  readHoursByBranch,
} from "../services/branchSchedule.service.ts";
import type { BranchReportMeta } from "./operationalDayReporteChequeo.ts";

export type { BranchReportMeta };

export function parseTimeToMinutes(time: string): number {
  const parts = String(time || "00:00:00").split(":").map(Number);
  const h = parts[0] || 0;
  const m = parts[1] || 0;
  const s = parts[2] || 0;
  return h * 60 + m + s / 60;
}

export function branchHoursToMeta(hours: any): BranchReportMeta {
  const nightScheduleEnabled = Boolean(hours?.nightScheduleEnabled);
  return {
    nightScheduleEnabled,
    cutoffMinutes: nightScheduleEnabled
      ? parseTimeToMinutes(String(hours?.operationalDayCutoffTime || "03:00:00"))
      : 3 * 60,
  };
}

export function branchIdsFromCheckRecords(
  records: ReporteChequeo[] | undefined | null,
  fallbackBranchId?: number
): number[] {
  const ids = new Set<number>();
  (records || []).forEach((record) => {
    const branchId = Number(record.branch_id);
    if (Number.isFinite(branchId) && branchId > 0) {
      ids.add(branchId);
    }
  });
  const fallback = Number(fallbackBranchId);
  if (Number.isFinite(fallback) && fallback > 0) {
    ids.add(fallback);
  }
  return [...ids];
}

const BRANCH_META_CACHE_TTL_MS = 10 * 60 * 1000;
const branchMetaCache = new Map<
  number,
  { value: BranchReportMeta; expiresAt: number }
>();
const branchMetaInflight = new Map<number, Promise<BranchReportMeta>>();

const DEFAULT_BRANCH_META: BranchReportMeta = {
  nightScheduleEnabled: false,
  cutoffMinutes: 3 * 60,
};

async function loadBranchReportMeta(
  branchId: number,
  session?: SessionLike
): Promise<BranchReportMeta> {
  const cached = branchMetaCache.get(branchId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  let request = branchMetaInflight.get(branchId);
  if (!request) {
    request = (async () => {
      try {
        const hours = can("attendance.view", session)
          ? await readHoursByBranch(branchId)
          : can("attendance.check", session)
            ? await readClockHoursByBranch(branchId)
            : null;
        if (!hours) {
          return DEFAULT_BRANCH_META;
        }
        const meta = branchHoursToMeta(hours);
        branchMetaCache.set(branchId, {
          value: meta,
          expiresAt: Date.now() + BRANCH_META_CACHE_TTL_MS,
        });
        return meta;
      } catch {
        return DEFAULT_BRANCH_META;
      } finally {
        branchMetaInflight.delete(branchId);
      }
    })();
    branchMetaInflight.set(branchId, request);
  }

  return request;
}

export async function loadBranchReportMetaMap(
  branchIds: number[],
  session?: SessionLike
): Promise<Record<number, BranchReportMeta>> {
  const unique = [...new Set(branchIds.filter((id) => id > 0))];
  if (unique.length === 0) {
    return {};
  }

  const entries = await Promise.all(
    unique.map(
      async (branchId) =>
        [branchId, await loadBranchReportMeta(branchId, session)] as const
    )
  );

  const map: Record<number, BranchReportMeta> = {};
  entries.forEach(([branchId, meta]) => {
    map[branchId] = meta;
  });
  return map;
}

export function resolveEffectiveBranchMeta(
  records: ReporteChequeo[] | undefined | null,
  loaded: Record<number, BranchReportMeta>,
  fallbackBranchId?: number
): Record<number, BranchReportMeta> {
  const branchIds = branchIdsFromCheckRecords(records, fallbackBranchId);
  const effective: Record<number, BranchReportMeta> = {};
  for (const id of branchIds) {
    effective[id] = loaded[id] ?? {
      nightScheduleEnabled: false,
      cutoffMinutes: 3 * 60,
    };
  }
  return effective;
}
